'use client'

import { useRouter } from 'next/navigation'
import { Fragment, useEffect, useState } from 'react'

import { Badge } from '@wartek-id/badge'
import { Button } from '@wartek-id/button'
import { Icon } from '@wartek-id/icon'
import { Text } from '@wartek-id/text'

import { range } from '@/utils/array'
import { xssSafeString } from '@/utils/string'
import {
  trackClickViewMoreDataset,
  trackSearchDataset,
  trackViewDatasetList,
} from '@/utils/tracker/modules/dataset'
import {
  District,
  DistrictResponse,
  EducationLevel,
  FilterResponse,
} from '@/apis/types'
import { useFetchStatisticTables } from '@/apis/useStatistik'

import { UncontrolledSearchBox } from '@/components/CommonSearchBox/UncontrolledSearchBox'
import { ContainerSectionNoPadding } from '@/components/Container'
import { EmptyState } from '@/components/EmptyState'
import { ViewMoreButton } from '@/components/ViewMoreButton'

import { DatasetItem } from './Dataset/DatasetItem'
import { DatasetLoader } from './Dataset/DatasetLoader'
import { FilterEducationLevel } from './FilterSelect/FilterEducationLevel'
import { FilterSection } from './FilterSelect/FilterSection'
import { FilterWilayah } from './FilterSelect/FilterWilayah'
import { FilterYearPeriod } from './FilterSelect/FilterYearPeriod'
import { useFilterState } from './FilterSelect/useFilterState'

const DEFAULT_LIMIT = 10

export function DatasetList({
  dataDistricts,
  dataFilters,
  initialDistrict,
  initialJenjang,
  initialYear,
}: {
  dataDistricts: DistrictResponse
  dataFilters: FilterResponse
  initialDistrict: District
  initialJenjang: EducationLevel
  initialYear: number
}) {
  const router = useRouter()

  const [isFinishSetInitialData, setFinishSetInitialData] =
    useState<boolean>(false)

  const {
    // -- Query params
    queryKeyword,

    // -- Filters
    keyword,
    selectedWilayah,
    selectedJenjang,
    selectedTahunAjaran,

    // -- Change handlers
    handleChangeJenjang,
    handleChangeTahunAjaran,
    handleChangeWilayah,
    handleSetKeyword,

    // -- Get search params for updating state
    getFilterSearchParams,
  } = useFilterState({ dataFilters: dataFilters })

  const {
    status,
    data,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useFetchStatisticTables(
    {
      limit: DEFAULT_LIMIT,
      educationLevelId: initialJenjang?.id || 1,
      keyword,
    },
    {
      enabled: isFinishSetInitialData,
    }
  )

  const isEmptyState =
    status === 'success' &&
    data &&
    data.pages.length === 1 &&
    data.pages[0].data.length === 0

  const handleClickTerapkan = () => {
    const searchParams = getFilterSearchParams({})
    router.replace(searchParams, { scroll: false })
  }

  const handleSubmitSearch = (newKeyword) => {
    handleSetKeyword(newKeyword)
    if (newKeyword !== '') {
      trackSearchDataset({
        districtId: selectedWilayah?.id,
        districtLevel: selectedWilayah?.districtLevel,
        yearPeriod: selectedTahunAjaran,
        educationLevelName: selectedJenjang?.name,
        keyword: newKeyword,
      })
    }

    const searchParams = getFilterSearchParams({
      customKeyword: newKeyword,
    })

    router.replace(searchParams, { scroll: false })
  }

  // Select default wilayah
  useEffect(() => {
    if (initialDistrict && !isFinishSetInitialData) {
      handleChangeWilayah(initialDistrict)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDistrict])

  useEffect(() => {
    if (
      dataFilters &&
      initialYear &&
      initialJenjang &&
      !isFinishSetInitialData
    ) {
      handleChangeTahunAjaran({
        // @ts-ignore
        target: { value: initialYear },
      })

      // @ts-ignore
      handleChangeJenjang({
        // @ts-ignore
        target: {
          value: `${initialJenjang?.id}`,
        },
      })

      // Set initial keyword from query params
      if (queryKeyword) {
        const cleanKeyword = xssSafeString(queryKeyword)
        handleSetKeyword(cleanKeyword)
      }

      setFinishSetInitialData(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dataFilters,
    initialJenjang,
    initialYear,
    isFinishSetInitialData,
    queryKeyword,
  ])

  useEffect(() => {
    if (isFinishSetInitialData) {
      trackViewDatasetList({
        districtId: initialDistrict.id,
        districtLevel: initialDistrict.districtLevel,
        yearPeriod: initialYear,
        educationLevelName: initialJenjang.name,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFinishSetInitialData])

  return (
    <>
      <section className="flex flex-col bg-gray-10" id="search-section">
        <UncontrolledSearchBox
          initialValue={keyword}
          placeholder={`Cari Dataset: “Jumlah siswa menurut jenis kelamin dan status sekolah”`}
          onSubmit={handleSubmitSearch}
        />
      </section>

      <FilterSection
        FilterWilayah={
          <FilterWilayah
            data={dataDistricts}
            loadingStatus={dataDistricts ? 'success' : 'pending'}
            value={selectedWilayah}
            onChange={handleChangeWilayah}
          />
        }
        FilterYearPeriod={
          <FilterYearPeriod
            yearPeriod={dataFilters?.yearPeriod}
            loadingStatus={dataFilters ? 'success' : 'pending'}
            value={selectedTahunAjaran}
            onChange={handleChangeTahunAjaran}
          />
        }
        FilterEducationLevel={
          <FilterEducationLevel
            educationLevel={dataFilters?.educationLevel}
            loadingStatus={dataFilters ? 'success' : 'pending'}
            value={selectedJenjang}
            onChange={handleChangeJenjang}
          />
        }
        FilterStatus={null}
        ButtonApply={
          <Button
            color="black"
            onClick={handleClickTerapkan}
            className="max-w-[95px]"
            data-testid="btn-terapkan-filter"
            disabled={
              !isFinishSetInitialData || isFetching || isFetchingNextPage
            }
          >
            Terapkan
          </Button>
        }
        ButtonDownload={null}
      />

      <ContainerSectionNoPadding className="py-8" id="content-section">
        <div className="flex flex-col gap-6 px-4 md:px-0">
          <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
            <Text variant="subheading">DAFTAR TABEL</Text>
            <Badge variant="neutral">
              Pembaruan Terakhir: 30 November {initialYear}
            </Badge>
          </div>

          {status === 'pending' && (
            <>
              {range(1, DEFAULT_LIMIT).map((item) => (
                <DatasetLoader key={item} />
              ))}
            </>
          )}

          {status === 'error' && (
            <EmptyState
              title="Oops, data tidak ditemukan"
              description="Coba kata kunci lain atau setel ulang filter yang Anda terapkan"
            />
          )}

          {status === 'success' && (
            <>
              {(data.pages || []).map((page, indexPage) => (
                <Fragment key={indexPage}>
                  {page?.data?.map((item, indexRow) => (
                    <DatasetItem
                      key={item.id}
                      indexItem={`${indexPage + 1}-${indexRow + 1}`}
                      item={item}
                      educationLevel={item.educationLevel}
                      district={initialDistrict}
                      year={initialYear}
                    />
                  ))}
                </Fragment>
              ))}

              {isEmptyState ? (
                <EmptyState
                  title="Oops, data tidak ditemukan"
                  description="Coba kata kunci lain atau setel ulang filter yang Anda terapkan"
                  size="sm"
                >
                  <div className="text-center">
                    <Button
                      onClick={() => {
                        window.location.assign('/dataset#search-section')
                      }}
                    >
                      Reset Pilihan
                      <Icon color="inverse" className="ml-2">
                        refresh
                      </Icon>
                    </Button>
                  </div>
                </EmptyState>
              ) : (
                <div>
                  <ViewMoreButton
                    onClick={() => {
                      trackClickViewMoreDataset()
                      fetchNextPage()
                    }}
                    hasNextPage={hasNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    isFetching={isFetching}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </ContainerSectionNoPadding>
    </>
  )
}
